import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from "react";

import GlobalSearchItem from "@app/types/globalSearch/GlobalSearchItem";
import useInterval from "@app/util/hook/useInterval";

// Keys typed in another dialog or notification reach the body too; only the search's own count,
// including those on its wrapper, which takes the focus when a row is clicked
const isSearchEvent = (e: KeyboardEvent, containerClassName: string) => {
  const selector = `.${containerClassName}`;
  return (
    e.target instanceof Element &&
    !!(e.target.closest(selector) || e.target.querySelector(selector))
  );
};

const useGlobalSearchKeyboard = ({
  containerClassName,
  items,
  onEscape,
  onSelect,
  selectedIndex,
  setSelectedIndex,
}: {
  containerClassName: string;
  items: GlobalSearchItem[];
  onEscape: () => void;
  onSelect: (item: GlobalSearchItem) => void;
  selectedIndex: number;
  setSelectedIndex: Dispatch<SetStateAction<number>>;
}) => {
  const [direction, setDirection] = useState<-1 | 0 | 1>(0);
  // An Enter that closed another layer on keydown (the naming card's buttons) lands its keyup here
  const enterPressedRef = useRef(false);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        // Not one on the modal's own buttons (a star, a chip), nor one ending an input-method
        // composition, which Safari marks only with keyCode 229
        enterPressedRef.current =
          e.target instanceof Element &&
          !e.target.closest("a, button") &&
          isSearchEvent(e, containerClassName) &&
          !e.isComposing &&
          e.keyCode !== 229;
      } else if (
        (e.code === "ArrowUp" || e.code === "ArrowDown") &&
        isSearchEvent(e, containerClassName)
      ) {
        setDirection(e.code === "ArrowUp" ? -1 : 1);
      }
    },
    [containerClassName]
  );

  const handleKeyUp = useCallback(
    (e: KeyboardEvent) => {
      if (!isSearchEvent(e, containerClassName)) {
        return;
      }
      if (e.code === "ArrowUp" || e.code === "ArrowDown") {
        setDirection(0);
      } else if (e.key === "Enter") {
        const item = items[selectedIndex];
        if (enterPressedRef.current && item) {
          onSelect(item);
        }
        enterPressedRef.current = false;
      } else if (e.code === "Escape") {
        onEscape();
      }
    },
    [containerClassName, items, onEscape, onSelect, selectedIndex]
  );

  const changeItemIndex = useCallback(() => {
    setSelectedIndex((index) => {
      const newIndex = (index + direction + items.length) % items.length;
      const element = document.querySelector(`[data-global-search-index='${newIndex}'`);
      if (element) {
        element.scrollIntoView({ behavior: "auto", block: "center" });
      }
      return newIndex;
    });
  }, [direction, items, setSelectedIndex]);

  useInterval(changeItemIndex, direction !== 0, 100, true, 400);

  useEffect(() => {
    document.body.addEventListener("keyup", handleKeyUp);
    document.body.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.removeEventListener("keyup", handleKeyUp);
      document.body.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown, handleKeyUp]);
};

export default useGlobalSearchKeyboard;
